import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import {
  CheckmarkCircleRegular, DismissCircleRegular, ErrorCircleRegular, ArrowClockwiseRegular, ServerRegular, DatabaseRegular, BrainRegular,
  TranslateRegular, GlobeRegular, PhoneRegular, ClockRegular, FlashRegular, PulseRegular, ArrowSyncRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const CheckCircle2 = fluentIcon(CheckmarkCircleRegular);
const XCircle = fluentIcon(DismissCircleRegular);
const AlertCircle = fluentIcon(ErrorCircleRegular);
const RefreshCw = fluentIcon(ArrowClockwiseRegular);
const Server = fluentIcon(ServerRegular);
const Database = fluentIcon(DatabaseRegular);
const Brain = fluentIcon(BrainRegular);
const Languages = fluentIcon(TranslateRegular);
const Globe = fluentIcon(GlobeRegular);
const Smartphone = fluentIcon(PhoneRegular);
const Clock = fluentIcon(ClockRegular);
const Zap = fluentIcon(FlashRegular);
const Activity = fluentIcon(PulseRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);

const BACKEND_URL = "https://tomitra-backend-187545700403.asia-south1.run.app";
const TRANSLATOR_URL = "https://tomitra-translator-187545700403.asia-south1.run.app";
const INFERENCE_PHI_URL = "https://tomitra-inference-phi-187545700403.asia-south1.run.app";
const INFERENCE_3B_URL = "https://tomitra-inference-3b-187545700403.asia-south1.run.app";
const INFERENCE_7B_URL = "https://tomitra-inference-187545700403.asia-south1.run.app";

interface HealthResponse {
  status: string;
  timestamp: string;
  version: string;
  connectivity: Record<string, string>;
}

interface ServiceHealth {
  name: string;
  url: string;
  icon: any;
  description: string;
  status: "ok" | "error" | "loading" | "idle";
  latency?: number;
  details?: Record<string, string>;
  error?: string;
}

async function checkService(url: string): Promise<{ ok: boolean; latency: number; data?: any; error?: string }> {
  const t0 = Date.now();
  try {
    const res = await fetch(`${url}/health`, { signal: AbortSignal.timeout(10000) });
    const latency = Date.now() - t0;
    if (!res.ok) return { ok: false, latency, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { ok: true, latency, data };
  } catch (e: any) {
    return { ok: false, latency: Date.now() - t0, error: e.message?.includes("timeout") ? "Timeout (cold start?)" : e.message };
  }
}

export default function HealthPage() {
  const { token } = useAuth();
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [checking, setChecking] = useState(false);

  const { data: backendHealth, refetch: refetchBackend, dataUpdatedAt } = useQuery<HealthResponse>({
    queryKey: ["health"],
    queryFn: () => adminGet<HealthResponse>("/health", token),
    refetchInterval: 30_000,
  });

  // AI analytics for the summary cards
  const { data: aiStats } = useQuery<any>({
    queryKey: ["health-ai"],
    queryFn: () => adminGet<any>("/api/v1/admin/stats/ai", token),
    refetchInterval: 60_000,
  });

  const { data: ingestionStats } = useQuery<any>({
    queryKey: ["health-ingestion-logs"],
    queryFn: () => adminGet<any>("/api/v1/admin/stats/ingestion-logs", token),
    refetchInterval: 60_000,
  });

  const checkAllServices = async () => {
    setChecking(true);
    try {
      const result = await adminGet<{ services: any[] }>("/api/v1/admin/service-health", token);
      const iconMap: Record<string, any> = {
        "Backend API": Server,
        "NLLB Translator": Languages,
        "Phi-3.5 LLM": Brain,
        "Qwen 3B LLM": Brain,
        "Qwen 7B LLM": Brain,
      };
      const mapped: ServiceHealth[] = result.services.map(s => {
        const details: Record<string, string> = {};
        if (s.details) {
          if (s.details.model) details["Model"] = s.details.model;
          if (s.details.version) details["Version"] = s.details.version;
          if (s.details.n_ctx) details["Context"] = `${s.details.n_ctx} tokens`;
          if (s.details.connectivity) {
            for (const [k, v] of Object.entries(s.details.connectivity)) {
              details[k] = v as string;
            }
          }
        }
        return {
          name: s.name,
          url: s.url,
          icon: iconMap[s.name] ?? Server,
          description: s.description,
          status: s.status as any,
          latency: s.latency_ms,
          details: Object.keys(details).length > 0 ? details : undefined,
          error: s.error ?? undefined,
        };
      });
      setServices(mapped);
    } catch (e: any) {
      console.error("Service health check failed:", e);
    }
    setChecking(false);
    refetchBackend();
  };

  const lastIngestion = ingestionStats?.logs?.[0];
  const ai24h = aiStats?.summary_24h;
  const activeServices = services.filter(s => s.status === "ok").length;
  const totalServices = services.filter(s => s.status !== "loading").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">System Health</h1>
          <p className="text-sm text-slate-500 mt-1">Live status of all Tomitra services. Click refresh to probe every endpoint.</p>
        </div>
        <button
          onClick={checkAllServices}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 bg-brand-blue hover:bg-blue-600 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
        >
          {checking ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {checking ? "Checking..." : "Check All Services"}
        </button>
      </div>

      {/* Overall status banner */}
      {backendHealth && (
        <div className={`rounded-xl p-4 mb-6 border ${
          backendHealth.status === "ok"
            ? "bg-emerald-950/20 border-emerald-900/40"
            : "bg-red-950/20 border-red-900/40"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {backendHealth.status === "ok"
                ? <CheckCircle2 size={24} className="text-emerald-400" />
                : <XCircle size={24} className="text-red-400" />
              }
              <div>
                <div className="text-sm font-semibold text-slate-200">
                  {backendHealth.status === "ok" ? "Backend Operational" : "Backend Issues Detected"}
                </div>
                <div className="text-xs text-slate-400">
                  v{backendHealth.version} · Last checked {dataUpdatedAt ? `${Math.round((Date.now() - dataUpdatedAt) / 1000)}s ago` : "—"}
                </div>
              </div>
            </div>
            {totalServices > 0 && (
              <div className="text-right">
                <div className="text-lg font-bold text-slate-200">{activeServices}/{totalServices}</div>
                <div className="text-[10px] text-slate-500">Services healthy</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick stats row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <QuickStat icon={<Activity size={14} />} label="Backend" value={backendHealth?.connectivity?.db ?? "—"} ok={backendHealth?.connectivity?.db === "ok"} />
        <QuickStat icon={<Zap size={14} />} label="AI Calls (24h)" value={ai24h?.total_calls?.toString() ?? "—"} ok={true} />
        <QuickStat icon={<Clock size={14} />} label="Avg AI Latency" value={ai24h ? `${(ai24h.avg_latency_ms / 1000).toFixed(1)}s` : "—"} ok={ai24h?.avg_latency_ms < 5000} />
        <QuickStat
          icon={<RefreshCw size={14} />}
          label="Last Ingestion"
          value={lastIngestion ? `${lastIngestion.articles_ingested} articles` : "—"}
          ok={lastIngestion?.status === "success"}
          sub={lastIngestion ? new Date(lastIngestion.created_at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" }) : undefined}
        />
      </div>

      {/* Backend connectivity */}
      {backendHealth && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-slate-300 mb-4">Backend Connectivity</h3>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(backendHealth.connectivity).map(([name, value]) => {
              const isOk = (value as string).startsWith("ok");
              return (
                <div key={name} className={`rounded-lg p-3 border ${isOk ? "bg-emerald-950/10 border-emerald-900/30" : "bg-red-950/10 border-red-900/30"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-300 capitalize">{name}</span>
                    {isOk ? <CheckCircle2 size={12} className="text-emerald-400" /> : <XCircle size={12} className="text-red-400" />}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono mt-1">{value as string}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All services health check */}
      {services.length > 0 && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-slate-300 mb-4">Service Health Check</h3>
          <div className="space-y-3">
            {services.map(s => (
              <div key={s.name} className={`rounded-lg p-4 border ${
                s.status === "ok" ? "bg-emerald-950/10 border-emerald-900/30" :
                s.status === "idle" ? "bg-slate-800/30 border-slate-700" :
                s.status === "loading" ? "bg-slate-800/30 border-slate-700" :
                "bg-red-950/10 border-red-900/30"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <s.icon size={16} className={
                      s.status === "ok" ? "text-emerald-400" :
                      s.status === "idle" ? "text-slate-500" :
                      s.status === "loading" ? "text-slate-500" :
                      "text-red-400"
                    } />
                    <div>
                      <div className="text-sm font-medium text-slate-200">{s.name}</div>
                      <div className="text-[10px] text-slate-500">{s.description}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      {s.status === "loading" && <Loader2 size={12} className="animate-spin text-slate-500" />}
                      {s.status === "ok" && <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={10} /> Healthy</span>}
                      {s.status === "error" && <span className="text-xs text-red-400 flex items-center gap-1"><XCircle size={10} /> Down</span>}
                      {s.status === "idle" && <span className="text-xs text-slate-500">Idle (scaled to 0)</span>}
                      {s.latency != null && <span className="text-[10px] text-slate-500 font-mono">{s.latency}ms</span>}
                    </div>
                    {s.error && s.status !== "idle" && <div className="text-[10px] text-red-400 mt-1">{s.error}</div>}
                  </div>
                </div>
                {s.details && Object.keys(s.details).length > 0 && (
                  <div className="flex gap-3 mt-2 pt-2 border-t border-slate-800/50">
                    {Object.entries(s.details).map(([k, v]) => (
                      <div key={k} className="text-[10px]">
                        <span className="text-slate-500">{k}: </span>
                        <span className={`font-mono ${(v as string).startsWith("ok") ? "text-emerald-400" : "text-slate-400"}`}>{v as string}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service URLs reference */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Service Endpoints</h3>
        <div className="space-y-2">
          {[
            { label: "Backend API", url: BACKEND_URL },
            { label: "Translator", url: TRANSLATOR_URL },
            { label: "Phi-3.5 LLM", url: INFERENCE_PHI_URL },
            { label: "Qwen 3B LLM", url: INFERENCE_3B_URL },
            { label: "Qwen 7B LLM", url: INFERENCE_7B_URL },
            { label: "Cloud SQL", url: "tomitra-prod:asia-south1:tomitra-db" },
            { label: "Admin", url: "https://admin.tomitra.com" },
            { label: "Website", url: "https://tomitra.com" },
          ].map(s => (
            <div key={s.label} className="flex items-center justify-between py-1.5 border-b border-slate-800/50">
              <span className="text-xs text-slate-400">{s.label}</span>
              <code className="text-[10px] text-slate-500 font-mono">{s.url.replace("https://", "")}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickStat({ icon, label, value, ok, sub }: { icon: React.ReactNode; label: string; value: string; ok?: boolean; sub?: string }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider mb-2">{icon} {label}</div>
      <div className={`text-lg font-semibold ${ok === false ? "text-red-400" : ok === true ? "text-emerald-400" : "text-slate-200"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
